import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import add from "../assets/images/restaurants/add.png";
import minus from "../assets/images/restaurants/minus.png";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import defaultImage from "../assets/images/cuisines/default.jpeg";
import RestaurantConflictModal from "../modal/RestaurantConflictModal";
import FoodTypeIcon from "./FoodTypeIcon";
import { orderService } from "../services";

type Props = {
  image: string;
  itemName: string;
  itemPrice: number;
  discountedPrice: number;
  deliveryTime: string;
  dishId: string;
  restaurantId: string;
  restaurantName?: string;
  initialQuantity?: number;
  initialCartItemId?: string;
  onCountChange?: (count: number) => void;
  status?: "closed" | "open now";
  foodType?: string | { name?: string } | null;
  /** When set, add/update items via group-order APIs (mobile AddItemModal parity) */
  groupSessionId?: string | null;
  groupItemId?: string | null;
  groupQuantity?: number;
  onGroupItemChanged?: () => void;
};

const RestaurantsItemCard = ({
  image,
  itemName,
  itemPrice,
  discountedPrice,
  deliveryTime,
  dishId,
  restaurantId,
  restaurantName,
  initialQuantity = 0,
  initialCartItemId,
  onCountChange,
  status,
  foodType,
  groupSessionId,
  groupItemId,
  groupQuantity = 0,
  onGroupItemChanged,
}: Props) => {
  const {
    getCartItemQuantity,
    addToCart,
    updateCartItem,
    getCartRestaurantId,
    clearCart,
    cartItems,
  } = useCart();

  const { checkAuth, openSigninModal } = useAuth();

  const [showConflictModal, setShowConflictModal] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [busy, setBusy] = useState(false);

  const cartQuantity = getCartItemQuantity(dishId, restaurantId);
  const [count, setCount] = useState<number>(
    groupSessionId ? groupQuantity || initialQuantity : cartQuantity || initialQuantity
  );

  useEffect(() => {
    if (groupSessionId) {
      setCount(groupQuantity || 0);
      onCountChange?.(groupQuantity || 0);
      return;
    }
    const quantity = getCartItemQuantity(dishId, restaurantId);
    setCount(quantity);
    if (onCountChange) {
      onCountChange(quantity);
    }
  }, [
    dishId,
    restaurantId,
    getCartItemQuantity,
    onCountChange,
    groupSessionId,
    groupQuantity,
  ]);

  const handleGroupAdd = async () => {
    if (!groupSessionId) return;
    setBusy(true);
    try {
      await orderService.addGroupOrderItem(groupSessionId, {
        dishId,
        quantity: 1,
      });
      setCount(1);
      onCountChange?.(1);
      toast.success("Added to group order");
      onGroupItemChanged?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to add item to group order");
    } finally {
      setBusy(false);
    }
  };

  const handleGroupUpdate = async (newCount: number) => {
    if (!groupSessionId) return;
    setBusy(true);
    try {
      if (newCount <= 0) {
        if (groupItemId) {
          await orderService.deleteGroupOrderItem(groupSessionId, groupItemId);
        }
        setCount(0);
        onCountChange?.(0);
      } else if (groupItemId) {
        await orderService.updateGroupOrderItem(groupSessionId, groupItemId, {
          quantity: newCount,
        });
        setCount(newCount);
        onCountChange?.(newCount);
      } else {
        await orderService.addGroupOrderItem(groupSessionId, {
          dishId,
          quantity: newCount,
        });
        setCount(newCount);
        onCountChange?.(newCount);
      }
      onGroupItemChanged?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update group item");
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (!checkAuth()) {
      openSigninModal();
      return;
    }

    if (groupSessionId) {
      await handleGroupAdd();
      return;
    }

    const cartRestaurantId = getCartRestaurantId();

    if (cartRestaurantId && cartRestaurantId !== restaurantId) {
      setShowConflictModal(true);
      setPendingAction(() => async () => {
        try {
          await clearCart();
          await addToCart(dishId, restaurantId, 1);
          setCount(1);
          onCountChange?.(1);
        } catch (error) {
          console.error("Failed to add to cart:", error);
        }
      });
      return;
    }

    try {
      await addToCart(dishId, restaurantId, 1);
      setCount(1);
      onCountChange?.(1);
    } catch (error) {
      console.error("Failed to add to cart:", error);
    }
  };

  const handleConfirmReplace = async () => {
    if (pendingAction) {
      await pendingAction();
    }
    setShowConflictModal(false);
    setPendingAction(null);
  };

  const handleCancelReplace = () => {
    setShowConflictModal(false);
    setPendingAction(null);
  };

  const handleIncrement = async () => {
    if (groupSessionId) {
      await handleGroupUpdate(count + 1);
      return;
    }
    const newCount = count + 1;
    try {
      await updateCartItem(dishId, restaurantId, newCount);
      setCount(newCount);
      onCountChange?.(newCount);
    } catch (error) {
      console.error("Failed to update cart:", error);
    }
  };

  const handleDecrement = async () => {
    if (count <= 0) return;
    if (groupSessionId) {
      await handleGroupUpdate(count - 1);
      return;
    }
    const newCount = count - 1;
    try {
      await updateCartItem(dishId, restaurantId, newCount);
      setCount(newCount);
      onCountChange?.(newCount);
    } catch (error) {
      console.error("Failed to update cart:", error);
    }
  };

  return (
    <div className="py-[20px] border-b-2 dark:border-gray-700 border-dashed transition-colors duration-300">
      <div className="flex justify-between items-center">
        <div className="flex gap-[10px] md:gap-[20px] items-center">
          <div className="flex items-center gap-4">
            <img
              src={image || defaultImage}
              alt="Food Item"
              className="h-[75px] w-[75px] rounded-[12px]"
              onError={(e) => (e.currentTarget.src = defaultImage)}
            />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <FoodTypeIcon foodType={foodType} size={14} />
              <p className="text-[#050505] dark:text-white font-urbanist text-[15px] md:text-[19px] font-medium leading-[25px]">
                {itemName}
              </p>
            </div>
            <p className="text-[#7402A7] dark:text-purple-400 font-urbanist text-[18px] font-medium leading-[22px]">
              {discountedPrice}{" "}
              <span className="line-through text-[#6A6A6A] dark:text-gray-400 ml-[15px]">
                {itemPrice}
              </span>
            </p>
            <p className="text-[#050505] dark:text-gray-300 font-urbanist text-[15px] font-normal leading-[22px]">
              {deliveryTime}
            </p>
          </div>
        </div>

        <div>
          {count === 0 ? (
            <button
              className={`px-4 py-2 bg-[#F5F5F5] dark:bg-gray-700 text-[#7402A7] dark:text-purple-400 rounded-[8px] font-urbanist font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                status === "closed" || busy ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={handleAdd}
              disabled={status === "closed" || busy}
            >
              {busy ? "..." : "Add"}
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <div
                className={`bg-[#F5F5F5] dark:bg-gray-700 text-[#7402A7] dark:text-purple-400 rounded-[8px] font-urbanist font-semibold text-[18px] w-[38px] h-[38px] flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                  busy ? "opacity-50" : ""
                }`}
                onClick={() => !busy && handleDecrement()}
              >
                <img
                  src={minus}
                  alt="minus"
                  className="h-[20px] w-[20px] dark:invert"
                />
              </div>
              <span className="text-white bg-[#7402A7] dark:bg-purple-600 font-urbanist text-[18px] font-semibold text-center rounded-[10px] px-[15px] py-[6px]">
                {count}
              </span>
              <div
                className={`bg-[#F5F5F5] dark:bg-gray-700 text-[#7402A7] dark:text-purple-400 rounded-[8px] font-urbanist font-semibold text-[18px] w-[38px] h-[38px] flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                  busy ? "opacity-50" : ""
                }`}
                onClick={() => !busy && handleIncrement()}
              >
                <img
                  src={add}
                  alt="add"
                  className="h-[20px] w-[20px] dark:invert"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {showConflictModal && (
        <RestaurantConflictModal
          onConfirm={handleConfirmReplace}
          onCancel={handleCancelReplace}
          currentRestaurantName={
            cartItems[0]?.restaurant?.businessName || "another restaurant"
          }
          newRestaurantName={restaurantName || "this restaurant"}
        />
      )}
    </div>
  );
};

export default RestaurantsItemCard;
